/**
 * @description
 * Custom React hook for managing client data.
 * Provides methods for fetching, creating, updating, and deleting clients.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 * @see admin/src/frontend/hooks/useClients.test.ts
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface Client {
  id: string;
  subdomain: string;
  name: string;
  status: 'active' | 'inactive' | 'suspended' | 'deploying';
  workerId: string | null;
  databaseId: string | null;
  kvNamespaceId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastDeployedAt: string | null;
}

interface UseClientsReturn {
  clients: Client[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createClient: (data: { subdomain: string; name: string; createdBy: string }) => Promise<Client>;
  updateClient: (id: string, data: Partial<Client>) => Promise<Client>;
  deleteClient: (id: string) => Promise<void>;
}

/**
 * Hook for managing clients
 */
export function useClients(): UseClientsReturn {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch all clients
   */
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getClients();
      setClients(data);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new client
   */
  const createClient = useCallback(async (data: { subdomain: string; name: string; createdBy: string }) => {
    const client = await api.createClient(data);
    setClients(prev => [...prev, client]);
    return client;
  }, []);

  /**
   * Update an existing client
   */
  const updateClient = useCallback(async (id: string, data: Partial<Client>) => {
    const updatedClient = await api.updateClient(id, data);
    setClients(prev => prev.map(c => c.id === id ? updatedClient : c));
    return updatedClient;
  }, []);

  /**
   * Delete a client
   */
  const deleteClient = useCallback(async (id: string) => {
    await api.deleteClient(id);
    setClients(prev => prev.filter(c => c.id !== id));
  }, []);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return {
    clients,
    loading,
    error,
    refetch: fetchClients,
    createClient,
    updateClient,
    deleteClient,
  };
}

/**
 * Hook for managing a single client
 */
export function useClient(id: string) {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch client by ID
   */
  const fetchClient = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getClient(id);
      setClient(data);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching client:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Update the client
   */
  const updateClient = useCallback(async (data: Partial<Client>) => {
    const updatedClient = await api.updateClient(id, data);
    setClient(updatedClient);
    return updatedClient;
  }, [id]);

  /**
   * Delete the client
   */
  const deleteClient = useCallback(async () => {
    await api.deleteClient(id);
    setClient(null);
  }, [id]);

  // Fetch client on mount or when ID changes
  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  return {
    client,
    loading,
    error,
    refetch: fetchClient,
    updateClient,
    deleteClient,
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */